import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { ContentCard } from "components/ContentCard";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import { AutoRuleTile } from "../components/AutoRuleTile";
import AutoRuleEditor from "../components/AutoRuleEditor";
import ruleEngine from "services/apis/ruleEngine";
import rulesApi from "services/apis/rulesApi";
import { getPathPrefix } from "utils/coreUtils";
import styles from "../styles/ConfigAutoRule.module.scss";

export default function ConfigAutoRule() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const versionId = params?.versionId;
  const versionName = location?.state?.versionName ?? "";
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [autoRules, setAutoRules] = useState([]);
  const [activeTile, setActiveTile] = useState(null);
  const [autoRuleMessage, setAutoRuleMessage] = useState("");
  useEffect(() => {
    async function getAutoRules() {
      setIsLoading(true);
      try {
        const response = await rulesApi.getAutoRules(
          localStorage.getItem("companyId"),
          versionId
        );
        setAutoRules(
          response?.data.map((rule) => {
            return { name: rule.name, id: rule.rulesId };
          })
        );
        const getAutoRuleText =
          await ruleEngine.getAutoReplyTextByDsConfigVersionId({
            dsConfigVersionId: versionId,
          });
        const autoRuleText = getAutoRuleText?.data?.autoReplyText;
        if (autoRuleText) {
          setAutoRuleMessage(autoRuleText);
        }
      } catch (error) {
        console.error(error);
      }
      setIsLoading(false);
    }
    getAutoRules();
  }, []);
  if (isLoading) {
    return;
  }
  return (
    <ContentCard
      classes={`${styles["content"]}`}
      headerImgSrc="/assets/images/engine.svg"
      title={`${t("versionsForm.configAutoRule")} ${versionName ? "-" : ""} ${versionName}`}
      bodyContent={
        <div className={`${styles["container"]}`}>
          <DndContext
            onDragStart={(event) => {
              const tileData = event.active.data.current;
              setActiveTile(tileData); // capture id and name
            }}
            onDragEnd={() => {
              setActiveTile(null);
            }}
          >
            <div className={`card ${styles["rule-list"]}`}>
              <div className={`${styles["header"]} card-header`}>
                {t("versionsForm.ruleListHeader")}
              </div>
              <div className="card-body">
                {autoRules.map((rule) => {
                  return (
                    <AutoRuleTile
                      key={rule.id}
                      id={rule.id}
                      name={rule.name}
                      classes={`${styles["rule-tile"]} mb-3`}
                    />
                  );
                })}
              </div>
            </div>
            <div className={`card ${styles["editor"]}`}>
              <div className={`${styles["header"]} card-header`}>
                {t("versionsForm.ruleEditorHeader")}
                <OverlayTrigger
                  placement="auto"
                  overlay={(props) => (
                    <Tooltip id="master-toggle-tooltip" {...props}>
                      {t("versionsForm.ruleEditorInfoText")}
                    </Tooltip>
                  )}
                >
                  <i className="fa fa-info-circle"></i>
                </OverlayTrigger>
              </div>
              <div className={`${styles["body"]} card-body`}>
                <AutoRuleEditor
                  onTokenDrop={(value) => {
                    setAutoRuleMessage(value);
                  }}
                  editorValue={autoRuleMessage}
                />
              </div>
            </div>
            <DragOverlay>
              {activeTile ? (
                <AutoRuleTile
                  id={activeTile.id}
                  name={activeTile.name}
                  classes={`${styles["rule-tile"]}`}
                  isOverlay={true}
                />
              ) : null}
            </DragOverlay>
          </DndContext>

          <div className={`${styles["actions"]}`}>
            <PrimaryButton
              title={t("CommonKeys.backButtonTitle")}
              icon="fa-solid fa-arrow-left"
              onClick={() => {
                navigate(
                  getPathPrefix("selected-datastream/rule-engine/versions")
                );
              }}
              className="primaryButton"
            />
            <PrimaryButton
              title={t("CommonKeys.saveButtonTitle")}
              onClick={async () => {
                await ruleEngine.saveOrUpdateAutoReplyText({
                  autoReplyText: autoRuleMessage,
                  dsConfigVersionId: versionId,
                });
                navigate(
                  getPathPrefix("selected-datastream/rule-engine/versions")
                );
              }}
              className="primaryButton"
              disabled={!autoRuleMessage}
            />
          </div>
        </div>
      }
    />
  );
}
